import { fromZonedTime, toZonedTime } from 'date-fns-tz';

/**
 * Builds a UTC Date from a calendar date and a "h:mm AM/PM" wall-clock
 * string. When `timezone` is provided, the wall-clock is interpreted in
 * that zone — so a buyer in PT booking a GA listing at "2:00 PM" stores
 * 14:00 ET (= 18:00 UTC), not 2 PM PT.
 */
export const combineDateTimeInZone = (
  date: Date,
  timeStr: string,
  timezone?: string
): Date => {
  if (!date || isNaN(date.getTime())) return new Date('');
  if (!timeStr || !/^\d{1,2}:\d{2} (AM|PM)$/.test(timeStr)) return new Date('');

  const [time, modifier] = timeStr.split(' ');
  const [rawHours, minutes] = time.split(':').map(Number);
  let hours = rawHours;
  if (modifier === 'PM' && hours !== 12) hours += 12;
  if (modifier === 'AM' && hours === 12) hours = 0;

  const wallClock = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes,
    0,
    0
  );
  return timezone ? fromZonedTime(wallClock, timezone) : wallClock;
};

/**
 * Renders the wall-clock portion of `date` in `timezone` as a "h:mm AM/PM"
 * string, rounded to the nearest 15 minutes. Used to populate the time
 * dropdown when editing an existing showing.
 */
export const formatWallClockInZone = (
  date: Date | string,
  timezone?: string
): string => {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '12:00 PM';
  const zoned = timezone ? toZonedTime(d, timezone) : d;
  const minutes = zoned.getMinutes();
  const roundedMinutes = Math.round(minutes / 15) * 15;
  const rounded = new Date(zoned);
  rounded.setMinutes(roundedMinutes);
  if (roundedMinutes === 60) {
    rounded.setHours(rounded.getHours() + 1);
    rounded.setMinutes(0);
  }
  return rounded.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};
